import {
  PriceList,

  Product,
  CreatePriceListRequest,
  UpdatePriceListRequest,

  PriceListFilters,
  ProductFilters,
  PriceListResponse,
  PriceListDetailResponse,
  ProductListResponse,
  CustomerGroupListResponse,
  PriceCalculationRequest,
  PriceCalculationResponse,
  PriceListValidation,
  PricingStatsResponse,
  ExportPriceListOptions,
  BulkPriceUpdateRequest,
  BulkPriceUpdateResponse,
  CustomerSelectionItem,
  ProductSelectionItem,
  PriceListStatus,

  PriceSource,
  ValidationError,
  ValidationWarning,
  PriceListConflict,
  BulkUpdateError,
} from '@/types/pricing';

import {
  mockPriceLists,
  mockPriceListLines,
  mockProducts,
  mockCustomerSelectionItems,
  mockProductSelectionItems,

  mockProductCategories,
  generatePriceListCode,

  calculateFinalPrice,
  getPriceListPriority,
} from '@/api/mockData/pricing';
import { mockCustomerGroups } from '@/api/mockData/customers';

// Simulate API delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to filter and paginate data
const filterAndPaginate = <T>(
  data: T[],
  page: number = 1,
  pageSize: number = 10,
  filterFn?: (item: T) => boolean
) => {
  const filteredData = filterFn ? data.filter(filterFn) : data;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  
  return {
    data: filteredData.slice(startIndex, endIndex),
    total: filteredData.length,
    page,
    pageSize,
  };
};

export const pricingApi = {
  // ===== PRICE LISTS =====
  
  // Get price lists with filters and pagination
  getPriceLists: async (
    page: number = 1,
    pageSize: number = 10,
    filters?: PriceListFilters
  ): Promise<PriceListResponse> => {
    await delay(500);

    const filterFn = (priceList: PriceList) => {
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = (
          priceList.code.toLowerCase().includes(searchLower) ||
          priceList.name.toLowerCase().includes(searchLower) ||
          (priceList.description && priceList.description.toLowerCase().includes(searchLower))
        );
        if (!matchesSearch) {
          return false;
        }
      }
      if (filters?.status && filters.status.length > 0 && !filters.status.includes(priceList.status)) {
        return false;
      }
      if (filters?.customerId && !priceList.applicableCustomers.includes(filters.customerId)) {
        return false;
      }
      if (filters?.customerGroupId && !priceList.applicableCustomerGroups.includes(filters.customerGroupId)) {
        return false;
      }
      if (filters?.isDefault !== undefined && priceList.isDefault !== filters.isDefault) {
        return false;
      }
      if (filters?.isActive !== undefined && priceList.isActive !== filters.isActive) {
        return false;
      }
      if (filters?.startDateFrom && priceList.startDate < filters.startDateFrom) {
        return false;
      }
      if (filters?.startDateTo && priceList.startDate > filters.startDateTo) {
        return false;
      }
      if (filters?.priority && priceList.priority !== filters.priority) {
        return false;
      }
      return true;
    };

    return filterAndPaginate(mockPriceLists, page, pageSize, filterFn);
  },

  // Get single price list by ID
  getPriceList: async (id: string): Promise<PriceList> => {
    await delay(300);
    
    const priceList = mockPriceLists.find(p => p.id === id);
    if (!priceList) {
      throw new Error(`Price list with ID ${id} not found`);
    }
    return priceList;
  },

  // Get price list detail with related data
  getPriceListDetail: async (id: string): Promise<PriceListDetailResponse> => {
    await delay(500);
    
    const priceList = mockPriceLists.find(p => p.id === id);
    if (!priceList) {
      throw new Error(`Price list with ID ${id} not found`);
    }

    const applicableCustomers = mockCustomerSelectionItems.filter(c => 
      priceList.applicableCustomers.includes(c.id)
    );
    
    const applicableCustomerGroups = mockCustomerGroups.filter(g => 
      priceList.applicableCustomerGroups.includes(g.id)
    );

    const productIds = priceList.priceListLines.map(line => line.productId);
    const products = mockProducts.filter(p => productIds.includes(p.id));

    // Find conflicting price lists (same customers/groups with overlapping dates)
    const conflictingPriceLists = mockPriceLists.filter(p => {
      if (p.id === id) return false;
      
      // Check customer overlap
      const hasCustomerOverlap = p.applicableCustomers.some(customerId => 
        priceList.applicableCustomers.includes(customerId)
      );
      
      // Check group overlap
      const hasGroupOverlap = p.applicableCustomerGroups.some(groupId => 
        priceList.applicableCustomerGroups.includes(groupId)
      );
      
      if (!hasCustomerOverlap && !hasGroupOverlap) return false;
      
      // Check date overlap
      const priceListStart = new Date(priceList.startDate);
      const priceListEnd = priceList.endDate ? new Date(priceList.endDate) : new Date('9999-12-31');
      const pStart = new Date(p.startDate);
      const pEnd = p.endDate ? new Date(p.endDate) : new Date('9999-12-31');
      
      return pStart <= priceListEnd && pEnd >= priceListStart;
    });

    return {
      priceList,
      applicableCustomers,
      applicableCustomerGroups,
      services: products,
      conflictingPriceLists,
    };
  },

  // Create new price list
  createPriceList: async (data: CreatePriceListRequest): Promise<PriceList> => {
    await delay(800);
    
    const newPriceListId = `PL${String(mockPriceLists.length + 1).padStart(3, '0')}`;
    
    const newPriceList: PriceList = {
      id: newPriceListId,
      code: generatePriceListCode(),
      name: data.name,
      description: data.description,
      status: data.status,
      startDate: data.startDate,
      endDate: data.endDate,
      isDefault: data.isDefault || false,
      priority: data.priority || getPriceListPriority({
        applicableCustomers: data.applicableCustomers,
        applicableCustomerGroups: data.applicableCustomerGroups,
      } as PriceList),
      createdBy: 'Current User',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      
      applicableCustomers: data.applicableCustomers,
      applicableCustomerGroups: data.applicableCustomerGroups,
      pricingFormula: data.pricingFormula,
      basePriceListId: data.basePriceListId,
      
      priceListLines: data.priceListLines.map((line, index) => ({
        id: `${newPriceListId}_L${String(index + 1).padStart(3, '0')}`,
        priceListId: newPriceListId,
        productId: line.productId,
        productCode: mockProducts.find(p => p.id === line.productId)?.code || '',
        productName: mockProducts.find(p => p.id === line.productId)?.name || '',
        basePrice: mockProducts.find(p => p.id === line.productId)?.basePrice || 0,
        specialPrice: line.specialPrice,
        discountPercentage: line.discountPercentage,
        finalPrice: calculateFinalPrice(
          mockProducts.find(p => p.id === line.productId)?.basePrice || 0,
          line.specialPrice,
          line.discountPercentage
        ),
        unit: mockProducts.find(p => p.id === line.productId)?.unit || '',
        minQuantity: line.minQuantity,
        maxQuantity: line.maxQuantity,
        notes: line.notes,
        isActive: line.isActive !== false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })),
      
      totalProducts: data.priceListLines.length,
      totalCustomers: data.applicableCustomers.length,
      isActive: data.status === 'active',
    };

    mockPriceLists.push(newPriceList);
    return newPriceList;
  },

  // Update price list
  updatePriceList: async (data: UpdatePriceListRequest): Promise<PriceList> => {
    await delay(600);
    
    const index = mockPriceLists.findIndex(p => p.id === data.id);
    if (index === -1) {
      throw new Error(`Price list with ID ${data.id} not found`);
    }

    const updatedPriceList = {
      ...mockPriceLists[index],
      ...data,
      updatedAt: new Date().toISOString(),
    } as PriceList;

    mockPriceLists[index] = updatedPriceList;
    return updatedPriceList;
  },

  // Delete price list
  deletePriceList: async (id: string): Promise<void> => {
    await delay(400);
    
    const index = mockPriceLists.findIndex(p => p.id === id);
    if (index === -1) {
      throw new Error(`Price list with ID ${id} not found`);
    }

    const priceList = mockPriceLists[index];
    if (priceList.isDefault) {
      throw new Error('Cannot delete default price list');
    }

    mockPriceLists.splice(index, 1);
  },

  // Copy price list
  copyPriceList: async (id: string, newName: string): Promise<PriceList> => {
    await delay(600);
    
    const original = mockPriceLists.find(p => p.id === id);
    if (!original) {
      throw new Error(`Price list with ID ${id} not found`);
    }

    const newPriceListId = `PL${String(mockPriceLists.length + 1).padStart(3, '0')}`;

    const newPriceList: PriceList = {
      ...original,
      id: newPriceListId,
      code: generatePriceListCode(),
      name: newName,
      status: 'draft',
      isDefault: false,
      priceListLines: original.priceListLines.map(line => ({
        ...line,
        id: `${newPriceListId}_L${String(line.id.split('_L')[1] || '001')}`,
        priceListId: newPriceListId,
      })),
      createdBy: 'Current User',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    mockPriceLists.push(newPriceList);
    return newPriceList;
  },

  // Toggle price list status
  togglePriceListStatus: async (id: string): Promise<PriceList> => {
    await delay(400);
    
    const index = mockPriceLists.findIndex(p => p.id === id);
    if (index === -1) {
      throw new Error(`Price list with ID ${id} not found`);
    }

    const priceList = mockPriceLists[index];
    const newStatus: PriceListStatus = priceList.status === 'active' ? 'inactive' : 'active';

    const updatedPriceList = {
      ...priceList,
      status: newStatus,
      updatedAt: new Date().toISOString(),
    };

    mockPriceLists[index] = updatedPriceList;
    return updatedPriceList;
  },

  // Validate price list
  validatePriceList: async (data: CreatePriceListRequest | UpdatePriceListRequest): Promise<PriceListValidation> => {
    await delay(300);
    
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];
    const conflicts: PriceListConflict[] = [];
    
    // Basic validation
    if (!data.name || data.name.trim().length === 0) {
      errors.push({
        field: 'name',
        message: 'Price list name is required',
        code: 'REQUIRED_FIELD',
      });
    }
    
    if (data.startDate && data.endDate && new Date(data.startDate) >= new Date(data.endDate)) {
      errors.push({
        field: 'endDate',
        message: 'End date must be after start date',
        code: 'INVALID_DATE_RANGE',
      });
    }
    
    // Check for duplicate names
    if (data.name) {
      const existingPriceList = mockPriceLists.find(p => 
        p.name.toLowerCase() === data.name!.toLowerCase() && 
        ('id' in data ? p.id !== data.id : true)
      );
      
      if (existingPriceList) {
        errors.push({
          field: 'name',
          message: 'A price list with this name already exists',
          code: 'DUPLICATE_NAME',
        });
      }
    }
    
    // Check for overlapping dates with existing price lists
    if (data.startDate && data.endDate) {
      const overlappingPriceLists = mockPriceLists.filter(p => {
        if ('id' in data && p.id === data.id) return false;
        
        const pStart = new Date(p.startDate);
        const pEnd = p.endDate ? new Date(p.endDate) : new Date('9999-12-31');
        const newStart = new Date(data.startDate!);
        const newEnd = new Date(data.endDate!);
        
        return (newStart <= pEnd && newEnd >= pStart);
      });
      
      overlappingPriceLists.forEach(pl => {
        conflicts.push({
          type: 'overlapping_dates',
          message: `Overlapping dates with price list "${pl.name}"`,
          conflictingPriceListId: pl.id,
          conflictingPriceListName: pl.name,
          affectedProducts: [],
          affectedCustomers: [],
          recommendedAction: 'Adjust the date range or set different priority',
        });
      });
    }
    
    // Check for duplicate customer scope
    if ('applicableCustomers' in data && data.applicableCustomers && data.applicableCustomers.length > 0) {
      const duplicateScopePriceLists = mockPriceLists.filter(p => {
        if ('id' in data && p.id === data.id) return false;
        
        const commonCustomers = p.applicableCustomers.filter(c => 
          data.applicableCustomers!.includes(c)
        );
        
        return commonCustomers.length > 0;
      });
      
      duplicateScopePriceLists.forEach(pl => {
        conflicts.push({
          type: 'duplicate_customer_scope',
          message: `Overlapping customer scope with price list "${pl.name}"`,
          conflictingPriceListId: pl.id,
          conflictingPriceListName: pl.name,
          affectedProducts: [],
          affectedCustomers: data.applicableCustomers!,
          recommendedAction: 'Remove overlapping customers or adjust priority',
        });
      });
    }
    
    // Check for duplicate services in lines
    if ('priceListLines' in data && data.priceListLines && data.priceListLines.length > 0) {
      const productIds = data.priceListLines.map(line => line.productId);
      const duplicateProducts = productIds.filter((id, index) => productIds.indexOf(id) !== index);

      duplicateProducts.forEach(productId => {
        errors.push({
          field: 'priceListLines',
          message: `Duplicate product found: ${mockProducts.find(p => p.id === productId)?.name || productId}`,
          code: 'DUPLICATE_PRODUCT',
        });
      });
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      conflicts,
    };
  },

  // ===== SERVICES =====

  // Get products for pricing
  getProductsForPricing: async (
    page: number = 1,
    pageSize: number = 10,
    filters?: ProductFilters
  ): Promise<ProductListResponse> => {
    await delay(400);

    const filterFn = (product: Product) => {
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = (
          product.code.toLowerCase().includes(searchLower) ||
          product.name.toLowerCase().includes(searchLower) ||
          product.categoryName.toLowerCase().includes(searchLower)
        );
        if (!matchesSearch) {
          return false;
        }
      }
      if (filters?.categoryId && product.categoryId !== filters.categoryId) {
        return false;
      }
      if (filters?.status && filters.status.length > 0 && !filters.status.includes(product.status)) {
        return false;
      }
      if (filters?.isAvailableForPricing !== undefined && product.isAvailableForPricing !== filters.isAvailableForPricing) {
        return false;
      }
      if (filters?.priceFrom && product.basePrice < filters.priceFrom) {
        return false;
      }
      if (filters?.priceTo && product.basePrice > parseFloat(filters.priceTo)) {
        return false;
      }
      return true;
    };

    return filterAndPaginate(mockProducts, page, pageSize, filterFn);
  },

  // Get service categories
  getServiceCategories: async () => {
    await delay(200);
    return mockProductCategories;
  },

  // ===== CUSTOMER & GROUP SELECTION =====

  // Get customers for pricing selection
  getCustomersForPricing: async (
    search?: string,
    limit: number = 50
  ): Promise<CustomerSelectionItem[]> => {
    await delay(300);
    
    let customers = mockCustomerSelectionItems;
    
    if (search) {
      const searchLower = search.toLowerCase();
      customers = customers.filter(c => 
        c.code.toLowerCase().includes(searchLower) ||
        c.name.toLowerCase().includes(searchLower)
      );
    }

    return customers.slice(0, limit);
  },

  // Get customer groups for pricing
  getCustomerGroupsForPricing: async (): Promise<CustomerGroupListResponse> => {
    await delay(200);
    return {
      data: mockCustomerGroups,
      total: mockCustomerGroups.length,
      page: 1,
      pageSize: mockCustomerGroups.length,
    };
  },

  // Get products for selection in pricing setup
  getProductsForSelection: async (
    search?: string,
    categoryId?: string,
    excludePriceListId?: string,
    limit: number = 100
  ): Promise<ProductSelectionItem[]> => {
    await delay(300);

    let products = mockProductSelectionItems;
    
    if (search) {
      const searchLower = search.toLowerCase();
      products = products.filter(p =>
        p.code.toLowerCase().includes(searchLower) ||
        p.name.toLowerCase().includes(searchLower) ||
        p.categoryName.toLowerCase().includes(searchLower)
      );
    }

    if (categoryId) {
      const category = mockProductCategories.find(c => c.id === categoryId);
      if (category) {
        products = products.filter(p => p.categoryName === category.name);
      }
    }

    if (excludePriceListId) {
      products = products.filter(p => !p.conflictingPriceLists.includes(excludePriceListId));
    }

    return products.slice(0, limit);
  },

  // ===== PRICE CALCULATION =====

  // Calculate price for a customer and product
  calculatePrice: async (request: PriceCalculationRequest): Promise<PriceCalculationResponse> => {
    await delay(200);
    
    const { customerId, serviceId, date = new Date().toISOString() } = request;

    const product = mockProducts.find(p => p.id === serviceId);
    if (!product) {
      throw new Error(`Product with ID ${serviceId} not found`);
    }

    const customer = mockCustomerSelectionItems.find(c => c.id === customerId);
    if (!customer) {
      throw new Error(`Customer with ID ${customerId} not found`);
    }

    // Find applicable price lists by priority
    const applicablePriceLists = mockPriceLists
      .filter(priceList => {
        // Check if price list is active and within date range
        if (priceList.status !== 'active') return false;
        
        const effectiveDate = new Date(date);
        const startDate = new Date(priceList.startDate);
        const endDate = priceList.endDate ? new Date(priceList.endDate) : null;
        
        if (effectiveDate < startDate) return false;
        if (endDate && effectiveDate > endDate) return false;
        
        // Check if customer is in scope
        if (priceList.applicableCustomers.includes(customerId)) return true;
        if (customer.groupId && priceList.applicableCustomerGroups.includes(customer.groupId)) return true;
        if (priceList.isDefault && priceList.applicableCustomers.length === 0 && priceList.applicableCustomerGroups.length === 0) return true;
        
        return false;
      })
      .sort((a, b) => a.priority - b.priority); // Sort by priority (lower = higher priority)

    let appliedPrice = product.basePrice;
    let priceSource: PriceSource = 'default';
    let priceListId: string | undefined;
    let priceListName: string | undefined;
    let discountPercentage = 0;
    const calculation = [];

    // Apply the highest priority price list
    if (applicablePriceLists.length > 0) {
      const priceList = applicablePriceLists[0];
      const priceListLine = priceList.priceListLines.find(line => line.productId === serviceId);
      
      if (priceListLine) {
        calculation.push({
          step: 1,
          description: 'Base price',
          operation: 'base',
          value: product.basePrice,
          result: product.basePrice,
        });

        if (priceListLine.specialPrice && priceListLine.specialPrice > 0) {
          appliedPrice = priceListLine.specialPrice;
          calculation.push({
            step: 2,
            description: 'Special price applied',
            operation: 'set',
            value: priceListLine.specialPrice,
            result: priceListLine.specialPrice,
          });
        } else if (priceListLine.discountPercentage && priceListLine.discountPercentage > 0) {
          discountPercentage = priceListLine.discountPercentage;
          appliedPrice = product.basePrice * (1 - discountPercentage / 100);
          calculation.push({
            step: 2,
            description: `Discount ${discountPercentage}%`,
            operation: 'multiply',
            value: (100 - discountPercentage) / 100,
            result: appliedPrice,
          });
        }

        priceListId = priceList.id;
        priceListName = priceList.name;
        
        // Determine price source
        if (priceList.applicableCustomers.includes(customerId)) {
          priceSource = 'customer_specific';
        } else if (customer.groupId && priceList.applicableCustomerGroups.includes(customer.groupId)) {
          priceSource = 'customer_group';
        } else {
          priceSource = 'default';
        }
      }
    }

    const discountAmount = product.basePrice - appliedPrice;

    return {
      serviceId,
      customerId,
      basePrice: product.basePrice,
      appliedPrice,
      discountAmount,
      discountPercentage,
      priceSource,
      priceListId,
      priceListName,
      isSpecialPrice: appliedPrice !== product.basePrice,
      effectiveDate: date,
      calculation,
    };
  },

  // ===== BULK OPERATIONS =====

  // Bulk update prices
  bulkUpdatePrices: async (request: BulkPriceUpdateRequest): Promise<BulkPriceUpdateResponse> => {
    await delay(800);
    
    const priceList = mockPriceLists.find(p => p.id === request.priceListId);
    if (!priceList) {
      throw new Error(`Price list with ID ${request.priceListId} not found`);
    }
    
    const errors: BulkUpdateError[] = [];
    let updatedCount = 0;
    let skippedCount = 0;
    
    const targetLines = request.productIds && request.productIds.length > 0
      ? priceList.priceListLines.filter(line => request.productIds!.includes(line.productId))
      : priceList.priceListLines;
    
    targetLines.forEach(line => {
      try {
        let newPrice = line.basePrice;
        
        switch (request.operation) {
          case 'increase_percentage':
            newPrice = line.basePrice * (1 + request.value / 100);
            break;
          case 'decrease_percentage':
            newPrice = line.basePrice * (1 - request.value / 100);
            break;
          case 'increase_amount':
            newPrice = line.basePrice + request.value;
            break;
          case 'decrease_amount':
            newPrice = line.basePrice - request.value;
            break;
          case 'set_price':
            newPrice = request.value;
            break;
        }
        
        // Apply rounding if specified
        if (request.roundingRule) {
          switch (request.roundingRule) {
            case 'round':
              newPrice = Math.round(newPrice);
              break;
            case 'ceil':
              newPrice = Math.ceil(newPrice);
              break;
            case 'floor':
              newPrice = Math.floor(newPrice);
              break;
          }
        }
        
        if (newPrice < 0) {
          errors.push({
            productId: line.productId,
            productCode: line.productCode,
            error: 'Price cannot be negative',
          });
          skippedCount++;
        } else {
          line.specialPrice = newPrice;
          line.finalPrice = calculateFinalPrice(line.basePrice, line.specialPrice, line.discountPercentage);
          line.updatedAt = new Date().toISOString();
          updatedCount++;
        }
      } catch (error: unknown) {
        errors.push({
          productId: line.productId,
          productCode: line.productCode,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
        skippedCount++;
      }
    });
    
    return {
      success: errors.length === 0,
      updatedCount,
      skippedCount,
      errors,
    };
  },

  // ===== STATISTICS =====

  // Get pricing statistics
  getPricingStats: async (): Promise<PricingStatsResponse> => {
    await delay(300);
    
    const totalPriceLists = mockPriceLists.length;
    const activePriceLists = mockPriceLists.filter(p => p.status === 'active').length;
    const draftPriceLists = mockPriceLists.filter(p => p.status === 'draft').length;
    const inactivePriceLists = mockPriceLists.filter(p => p.status === 'inactive').length;
    
    const totalServices = mockProducts.length;
    const servicesWithSpecialPricing = mockProductSelectionItems.filter(p => p.hasSpecialPricing).length;
    
    const customersWithSpecialPricing = new Set();
    mockPriceLists.forEach(priceList => {
      priceList.applicableCustomers.forEach(customerId => {
        customersWithSpecialPricing.add(customerId);
      });
    });
    
    const allLines = mockPriceListLines.filter(line => line.discountPercentage && line.discountPercentage > 0);
    const averageDiscountPercentage = allLines.length > 0 
      ? allLines.reduce((sum, line) => sum + (line.discountPercentage || 0), 0) / allLines.length 
      : 0;

    const mostUsedPriceList = mockPriceLists.reduce((prev, current) => {
      return (prev.totalCustomers > current.totalCustomers) ? prev : current;
    });

    const now = new Date();
    const upcomingExpirations = mockPriceLists.filter(priceList => {
      if (!priceList.endDate) return false;
      const endDate = new Date(priceList.endDate);
      const daysUntilExpiry = (endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
      return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
    });

    return {
      totalPriceLists,
      activePriceLists,
      draftPriceLists,
      inactivePriceLists,
      totalServices,
      servicesWithSpecialPricing,
      totalCustomersWithSpecialPricing: customersWithSpecialPricing.size,
      averageDiscountPercentage,
      mostUsedPriceList,
      upcomingExpirations,
    };
  },

  // ===== EXPORT =====

  // Export price lists
  exportPriceLists: async (options: ExportPriceListOptions): Promise<Blob> => {
    await delay(1000);
    
    let dataToExport = mockPriceLists;
    
    if (options.priceListIds && options.priceListIds.length > 0) {
      dataToExport = dataToExport.filter(p => options.priceListIds!.includes(p.id));
    }
    
    if (!options.includeInactive) {
      dataToExport = dataToExport.filter(p => p.status !== 'inactive');
    }

    const exportData = {
      priceLists: dataToExport,
      priceListLines: options.includeLines ? mockPriceListLines : [],
      exportOptions: options,
      exportedAt: new Date().toISOString(),
      totalRecords: dataToExport.length,
    };
    
    const jsonData = JSON.stringify(exportData, null, 2);
    return new Blob([jsonData], { type: 'application/json' });
  },
}; 